import './Browse.scss';
import 'moment-timezone';
import ChannelChartStack from 'components/channel/ChannelChartStack';
import Http from 'components/http/Http';
import React from 'react';
import Server from 'data/Server';
import StaticChannel from 'components/channel/StaticChannel';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { TreeTable } from 'primereact/treetable';
import moment from 'moment';


const _LIST_TIME_SERIES_PATH = '/api/list_time_series';
const _GET_POINT_LIST_PATH = '/api/time_series/time_series_id/get_point_list';
const _KEY_TYPE = Object.freeze({
  day: 'day',
  dc: 'dc',
  hour: 'hour',
  user: 'user',
});
const _HIERARCHY_OPTIONS = Object.freeze([
  { label: 'Channel > Day > Hour', value: 'channel_day_hour', },
  { label: 'Day > Hour > Channel', value: 'day_hour_channel', },
]);


const _key = (type, id) => `${type}_${id}`;

const _split = (keyFn, dataFn, timeSeriesList) => {
  // Input: [{ts1}, {ts2}, {ts3}, {ts4}, ...]
  // Output: [{ key: keyVal1, data: {}, children: [{ts1}, {ts2},], }, ...]
  const output = {};
  timeSeriesList.forEach(ts => {
    const key = keyFn(ts);
    output[key] = output[key] || {
      key, data: dataFn(key, ts), children: [],
    };
    output[key].children.push(ts);
  });
  return Object.values(output)
      .sort((a, b) => {
        const aVal = a.data.sort || a.data.label;
        const bVal = b.data.sort || b.data.label;
        return aVal < bVal ? -1 : 1;
      });
};


class Browse extends React.Component {
  constructor() {
    super();
    this.http = new Http.Factory().create();
    this.state = {
      isAvailableTimeSeriesLoaded: false,
      isGraphDataLoaded: false,
      hierarchy: 'channel_day_hour',
      localTz: true,
      xAxisFit: true,
    };
    this.server = new Server.Factory().create();
  }

  _getDate(ts, format) {
    let m1 = moment.utc(ts.first_datetime);

    // TODO This hack puts all the data into April. Remove it.
    m1 = m1.month(3 /* april */).year(2020);

    if (this.state.localTz) {
      m1.local();
    }
    return m1.format(format);
  }

  async queryAvailableTimeSeries() {
    let result;
    try {
      result = await this.http.get(_LIST_TIME_SERIES_PATH);
    } catch (error) {
      console.log(error.status);
      console.log(error);
      return;
    }

    this.setState({
      isAvailableTimeSeriesLoaded: true, data: result.body.result,
    });
  }

  async queryGraphData(tsId) {
    let result;
    try {
      result = await this.http.get(
          _GET_POINT_LIST_PATH.replace('time_series_id', tsId));
      // result = {body: {result: 'abc'}};
    } catch (error) {
      console.log(error.status);
      console.log(error);
      return;
    }

    setTimeout(() => {
      this.setState({
        isGraphDataLoaded: true,
        graphData: result.body.result,
      });
    });
  }

  makeTimeSeriesLabel(ts, displayStr) {
    displayStr = displayStr || this._getDate(ts, 'h A');
    return (
      <div className='timeSeriesLabelWrapper'>
        <div className='timeSeriesLabel'>
          <div className='date'>{displayStr}</div>
          <i className='pi pi-angle-double-right' />
        </div>
      </div>
    );
  }

  makeTableData(data) {
    const tss = Object.values(data.time_series_map)
      .map(ts => Object.assign(ts, {
        key: _key(_KEY_TYPE.hour, ts.id),
        data: { label: this.makeTimeSeriesLabel(ts), },
        selectable: true,
      }));
    const daySplit = (keyPrefix, tss) => _split(
      ts => keyPrefix + _key(_KEY_TYPE.day, this._getDate(ts, 'YYYY-MM-DD')),
      (k, ts) => ({ label: this._getDate(ts, 'LL'), sort: k, }),
      tss
    );
    const channelSplit = (keyPrefix, tss) => _split(
      ts => keyPrefix + _key(_KEY_TYPE.dc, ts.data_channel_id),
      (k, ts) => ({
        label: data.data_channel_map[ts.data_channel_id].sensor_name,
      }),
      tss
    );
    const hourSplit = (keyPrefix, tss) => _split(
      ts => keyPrefix + _key(_KEY_TYPE.hour, this._getDate(ts, 'HH')),
      (k, ts) => ({
          label: this._getDate(ts, 'h A'), sort: this._getDate(ts, 'HH'),
      }),
      tss
    );
    const userSplit = (keyPrefix, tss) => _split(
      ts => keyPrefix + _key(_KEY_TYPE.user, this.server.user_email()),
      (k, ts) => ({ label: this.server.user_email(), }),
      tss
    );
    const nestChildren = (arr, keyPrefix, splitters) => {
      if (!splitters.length) {
        return arr;
      }
      const splitter = splitters.shift();
      const splitResult = splitter(keyPrefix, arr);
      splitResult.forEach(
          node => node.children = nestChildren(
              node.children, keyPrefix + ':' + node.key, [...splitters]));
      return splitResult;
    };

    let splitters = [];
    switch (this.state.hierarchy) {
      case 'day_hour_channel':
        splitters = [daySplit, hourSplit];
        tss.forEach(ts => {
          const label = data.data_channel_map[ts.data_channel_id].sensor_name;
          ts.data.label = this.makeTimeSeriesLabel(ts, label);
          ts.data.sort = label;
        });
        tss.sort((a, b) => a.data.sort < b.data.sort ? -1 : 1);
        break;
      case 'channel_day_hour':
      default:
        splitters = [channelSplit, daySplit];
    }
    if (this.state.orgView) {
      splitters = [userSplit, ...splitters];
    }
    return nestChildren(tss, '', splitters);
  }

  onRowSelect(e) {
    const rowId = e.value;
    // TODO make this more robust.
    const match = /^hour_(\d+)$/.exec(rowId);
    if (match) {
      this.setState({ showGraph: match[1] });
    } else {
      let expandedKeys = Object.assign({}, this.state.expandedKeys);
      expandedKeys[rowId] ?
          delete expandedKeys[rowId] : expandedKeys[rowId] = true;
      this.setState({ expandedKeys });
    }
  }

  renderSelector() {
    return (
      <div className='section'>
        <div className='displayOptions'>
          <div className='displayOption left'>
            {/* TODO this orgView concept is kind of fake at the moment. */}
            <InputSwitch checked={this.state.orgView}
                tooltip={
                    'Showing ' +
                    (this.state.orgView ?
                        'all users for this organization.' :
                        'a single user.')}
                onChange={e => this.setState({ orgView: e.value })} />
            <span className='label'>
              {this.state.orgView ? 'Organization view' : 'User view'}
            </span>
          </div>
          <div className='displayOption left'>
            <InputSwitch checked={this.state.localTz}
                tooltip={
                    'Times are displayed in ' +
                    (this.state.localTz ? 'local' : 'UTC') + ' timezone.'}
                onChange={e => this.setState({ localTz: e.value })} />
            <span className='label'>
              {this.state.localTz ? 'Local times' : 'UTC times'}
            </span>
          </div>
          <div className='displayOption right'>
            <Dropdown value={this.state.hierarchy}
                options={_HIERARCHY_OPTIONS}
                onChange={e => this.setState({hierarchy: e.value})}
                placeholder='Select hierarchy' />
          </div>
        </div>
        <TreeTable
            value={this.makeTableData(this.state.data)}
            selectionMode='single'
            onSelectionChange={this.onRowSelect.bind(this)}
            expandedKeys={this.state.expandedKeys}
            onToggle={(e) => this.setState({ expandedKeys: e.value })}>
          <Column field='label' header='Selection' expander />
        </TreeTable>
      </div>
    );
  }

  renderGraph() {
    const tsId = this.state.showGraph;
    const ts = this.state.data.time_series_map[tsId];
    const dataChannel = this.state.data.data_channel_map[ts.data_channel_id];
    if (!this.state.isGraphDataLoaded) {
      this.queryGraphData(tsId);
      return <div className='section'>Loading…</div>;
    }
    const channel = new StaticChannel(
        dataChannel.sensor_name, dataChannel.units);
    channel.addData(this.state.graphData.time_series_point_list);
    // TODO:
    //   add buttons to scroll left/right/in/out
    //   allow to view local time

    let xAxisMin = moment.utc(ts.first_datetime).unix() * 1000;
    let xAxisMax = moment.utc(ts.first_datetime).add(1,'hour').unix() * 1000;
    if (this.state.xAxisFit) {
      xAxisMin = channel.getData()[0].t;
      xAxisMax = channel.getData()[channel.getData().length - 1].t;
    }
    return (
      <div className='section'>
        <div className='displayOptions'>
          <div className='displayOption left'>
            <Button
                label='Back' icon='pi pi-chevron-left'
                className='p-button-secondary'
                onClick={(e) => this.setState({
                  showGraph: false, isGraphDataLoaded: false,
                })} />
          </div>
          <div className='displayOption right'>
            <Button
                label='' icon='pi pi-chevron-left'
                className='navButton p-button-secondary' />
            <Button
                label='' icon='pi pi-minus'
                className='navButton p-button-secondary'
                onClick={e => this.setState({xAxisFit: false})} />
            <Button
                label='' icon='pi pi-plus'
                className='navButton p-button-secondary'
                onClick={e => this.setState({xAxisFit: true})} />
            <Button
                label='' icon='pi pi-chevron-right'
                className='navButton p-button-secondary' />
          </div>
        </div>
        <ChannelChartStack 
            xAxisMin={xAxisMin} xAxisMax={xAxisMax}
            channels={{[channel.getId()]: {channel}}} />
      </div>
    );
  }

  render() {
    if (!this.state.isAvailableTimeSeriesLoaded) {
      this.queryAvailableTimeSeries();
      return <div className='section'>Loading…</div>;
    } else {
      return (
        <div className='Browse'>
          {this.state.showGraph ?
              this.renderGraph() : this.renderSelector()}
        </div>
      );
    }
  }
}


export default Browse;
